import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, Calculator, FileText, Award, Globe, FileCheck, Home, ChevronDown, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { LocalizedLink } from "@/components/LocalizedLink";
import logoLegalizacneCentrum from "@/assets/logo-legalizacnecentrum.png";

export function MainNavbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const { t, language } = useLanguage();
  
  // Only use transparent/white text on homepage hero
  const isHomePage = location.pathname === "/" || location.pathname === `/${language}`;
  const useWhiteText = isHomePage && !isScrolled;

  const services = [
    { 
      nameKey: "nav.translations_short", 
      href: "/sluzby/uradne-preklady", 
      icon: FileText, 
    },
    { 
      nameKey: "nav.apostila", 
      href: "/sluzby/apostila", 
      icon: Award, 
    },
    { 
      nameKey: "nav.superlegalization", 
      href: "/sluzby/superlegalizacia", 
      icon: Globe, 
    },
    { 
      nameKey: "nav.foreign_documents", 
      href: "/sluzby/zahranicne-dokumenty", 
      icon: FileCheck, 
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top Contact Bar */}
      <div className="bg-primary text-primary-foreground text-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-12">
            {/* Contact info */}
            <div className="flex items-center gap-4 md:gap-6">
              <a href="tel:+421907999909" className="flex items-center gap-1.5 hover:text-secondary transition-colors">
                <Phone className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">+421 907 999 909</span>
              </a>
              <a href="mailto:office@legalizacnecentrum.sk" className="hidden sm:flex items-center gap-1.5 hover:text-secondary transition-colors">
                <Mail className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">office@legalizacnecentrum.sk</span>
              </a>
            </div>
            
            {/* Language & CTA */}
            <div className="flex items-center gap-2">
              <LanguageSwitcher isWhiteText={true} />
              <LocalizedLink to="/kalkulator" className="hidden sm:block">
                <Button size="sm" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold px-3 py-1.5 h-7 rounded text-xs">
                  <Calculator className="w-3.5 h-3.5 mr-1" />
                  {t('nav.calculator_short', 'Kalkulácia')}
                </Button>
              </LocalizedLink>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div
        className={cn(
          "transition-all duration-300 bg-white border-b border-border/50",
          isScrolled && "shadow-md"
        )}
      >
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-14">
            {/* Logo */}
            <LocalizedLink to="/" className="flex items-center gap-2 group">
              <img 
                src={logoLegalizacneCentrum} 
                alt="Apostila & Superlegalizácia - Legalizačné centrum Bratislava" 
                className="h-9 w-auto"
              />
            </LocalizedLink>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <LocalizedLink to="/" className="text-foreground/80 hover:text-secondary transition-colors font-medium">
                {t('nav.home', 'Domov')}
              </LocalizedLink>
              
              <LocalizedLink 
                to="/sluzby" 
                className="text-foreground/80 hover:text-secondary transition-colors font-medium"
              >
                {t('nav.services', 'Služby')}
              </LocalizedLink>

              <LocalizedLink to="/cennik" className="text-foreground/80 hover:text-secondary transition-colors font-medium">
                {t('nav.pricing', 'Cenník')}
              </LocalizedLink>
              <LocalizedLink to="/o-nas" className="text-foreground/80 hover:text-secondary transition-colors font-medium">
                {t('nav.about', 'O nás')}
              </LocalizedLink>
              <LocalizedLink to="/blog" className="text-foreground/80 hover:text-secondary transition-colors font-medium">
                Blog
              </LocalizedLink>
              <LocalizedLink to="/faq" className="text-foreground/80 hover:text-secondary transition-colors font-medium">
                FAQ
              </LocalizedLink>
              <LocalizedLink to="/kontakt" className="text-foreground/80 hover:text-secondary transition-colors font-medium">
                {t('nav.contact', 'Kontakt')}
              </LocalizedLink>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-foreground"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </nav>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden bg-card border-t border-border animate-slide-up">
            <div className="py-4 space-y-1">
              <LocalizedLink to="/" className="block px-4 py-3 text-foreground hover:bg-muted rounded-lg font-medium">
                <Home className="w-5 h-5 inline mr-2 text-secondary" />
                {t('nav.home', 'Domov')}
              </LocalizedLink>
              
              {/* Mobile Services Accordion */}
              <div className="px-4">
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="flex items-center justify-between w-full py-3 text-foreground font-medium"
                >
                  <span>{t('nav.services', 'Služby')}</span>
                  <ChevronDown className={cn(
                    "w-5 h-5 text-muted-foreground transition-transform duration-200",
                    mobileServicesOpen && "rotate-180"
                  )} />
                </button>
                
                {mobileServicesOpen && (
                  <div className="pl-4 pb-2 space-y-1 border-l-2 border-secondary/30 ml-2">
                    <LocalizedLink
                      to="/sluzby"
                      className="flex items-center gap-3 py-3 text-secondary font-medium hover:text-secondary/80 transition-colors"
                    >
                      {t('nav.services_overview', 'Prehľad služieb')}
                    </LocalizedLink>
                    {services.map((service) => (
                      <LocalizedLink
                        key={service.href}
                        to={service.href}
                        className="flex items-center gap-3 py-3 text-foreground hover:text-secondary transition-colors"
                      >
                        <service.icon className="w-5 h-5 text-secondary" />
                        {t(service.nameKey)}
                      </LocalizedLink>
                    ))}
                  </div>
                )}
              </div>
              
              <LocalizedLink to="/cennik" className="block px-4 py-3 text-foreground hover:bg-muted rounded-lg">
                {t('nav.pricing', 'Cenník')}
              </LocalizedLink>
              <LocalizedLink to="/o-nas" className="block px-4 py-3 text-foreground hover:bg-muted rounded-lg">
                {t('nav.about', 'O nás')}
              </LocalizedLink>
              <LocalizedLink to="/blog" className="block px-4 py-3 text-foreground hover:bg-muted rounded-lg">
                Blog
              </LocalizedLink>
              <LocalizedLink to="/faq" className="block px-4 py-3 text-foreground hover:bg-muted rounded-lg">
                {t('nav.faq', 'FAQ')}
              </LocalizedLink>
              <LocalizedLink to="/kontakt" className="block px-4 py-3 text-foreground hover:bg-muted rounded-lg">
                {t('nav.contact', 'Kontakt')}
              </LocalizedLink>
              <div className="px-4 pt-4">
                <LocalizedLink to="/kalkulator" className="block">
                  <Button className="w-full bg-gradient-to-r from-secondary to-primary text-primary-foreground font-bold py-5 rounded-xl">
                    <Calculator className="w-5 h-5 mr-2" />
                    {t('nav.calculator', 'Online kalkulácia')}
                  </Button>
                </LocalizedLink>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}